<script lang="ts" setup>
import { Studio, StudioUser, SearchParams } from '@/modules/Studios/types'
import { fetchStudioTeachers, deleteStudioTeacherById, addStudioTeacher } from '@/enitites/studio/api/endpoints'
import { ref, watch } from 'vue'
import UserChip from '@/shared/ui/chips/UserChip.vue'
import BKInput from '@/components/AppShared/BKInput.vue'
import { mdiMagnify, mdiMinusCircleOutline, mdiPlus } from '@mdi/js'
import { BKModal } from '@/shared/ui/modals'
import BKUserSelect from '@/components/BKUserSelect.vue'
import Bugsnag from '@bugsnag/js'

const props = defineProps<{ studio: Studio }>()

const studioTeachers = ref<StudioUser[]>([])
const infiniteId = ref(Number(new Date()))
const loading = ref(false)
const showNotFound = ref(false)
const searchParams = ref<SearchParams>({
  pageNumber: 0,
  pageSize: 100,
  keyword: '',
  sortBy: 'name',
  sortDir: 'asc',
})

const infiniteHandler = async ($state: { complete: () => void; loaded: () => void }) => {
  loading.value = true
  const data = await fetchStudioTeachers(props.studio.id, searchParams.value)
  loading.value = false

  if (!Array.isArray(data)) {
    $state.complete()
    return
  }
  searchParams.value.pageNumber += 1

  studioTeachers.value = [...studioTeachers.value, ...data]
  if (!studioTeachers.value.length) showNotFound.value = true
  if (data.length < searchParams.value.pageSize) $state.complete()
  else $state.loaded()
}

const searchTimeout = ref<number | null>(null)

const showAddModal = ref(false)
const isAddingTeacher = ref(false)
const newlyAddedTeachers = ref<StudioUser[]>([])

const handleAddUser = async (user: StudioUser) => {
  isAddingTeacher.value = true

  try {
    const res = await addStudioTeacher(props.studio.id, user.id)
    if (!res) return

    studioTeachers.value.push(user)
    newlyAddedTeachers.value.push(user)
  } catch (e) {
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)), (event) => {
      event.addMetadata('studio', {
        context: 'Error adding studio teacher',
        studioId: props.studio.id,
        userId: user.id,
      })
    })
  } finally {
    isAddingTeacher.value = false
  }
}

const handleDeleteUser = async (user: StudioUser) => {
  const res = await deleteStudioTeacherById(props.studio.id, user.id)
  if (res !== 'ok') return

  studioTeachers.value = studioTeachers.value.filter((u) => u.id !== user.id)
  newlyAddedTeachers.value = newlyAddedTeachers.value.filter((u) => u.id !== user.id)
}

watch(
  () => searchParams.value.keyword,
  () => {
    if (searchTimeout.value) clearTimeout(searchTimeout.value)
    searchTimeout.value = setTimeout(() => {
      searchParams.value.pageNumber = 0
      studioTeachers.value = []
      showNotFound.value = false
      infiniteId.value += 1
      searchTimeout.value = null
    }, 600)
  }
)
</script>

<template>
  <div class="teachers">
    <span class="editor-title">Teachers</span>

    <div class="search">
      <BKInput
        v-model="searchParams.keyword"
        :append-icon="mdiMagnify"
        label="Search current teachers"
        autocomplete="off"
        background-color="white"
        hide-details
        outlined
        single-line
      />
    </div>

    <div class="list">
      <UserChip
        v-for="user in studioTeachers"
        :key="JSON.stringify(user)"
        :user="user"
      >
        <template #actions>
          <v-icon
            class="close-icon"
            color="red"
            @click="() => handleDeleteUser(user)"
          >
            {{ mdiMinusCircleOutline }}
          </v-icon>
        </template>
      </UserChip>

      <infinite-loading
        :distance="400"
        :identifier="infiniteId"
        spinner="bubbles"
        @infinite="infiniteHandler"
      >
        <template #spinner>
          <v-progress-circular
            color="#FC7800"
            indeterminate
            size="80"
            width="10"
          />
        </template>
        <span slot="no-more" />
        <span slot="no-results" />
      </infinite-loading>
    </div>

    <div class="options">
      <v-btn
        color="var(--color-blue-800)"
        dark
        height="52"
        width="180"
        @click="showAddModal = true"
      >
        Add teacher
      </v-btn>
    </div>

    <BKModal
      :is-show="showAddModal"
      title="Add teacher"
      width="500"
      @update:isShow="
        (val) => {
          showAddModal = val
          if (!val) {
            newlyAddedTeachers = []
          }
        }
      "
    >
      <div class="modal-content">
        <p class="teachers-title">Add teachers</p>
        <BKUserSelect
          :exclude="studioTeachers.map((teacher) => teacher.id)"
          :select-user-chip="newlyAddedTeachers"
          label-text="Find teachers to add"
          @removeUser="handleDeleteUser"
          @selectUser="handleAddUser"
        />
      </div>
      <div class="modal-options">
        <v-btn
          color="var(--color-blue-800)"
          dark
          height="52"
          width="180"
          :disabled="!newlyAddedTeachers.length || isAddingTeacher"
          @click="showAddModal = false"
        >
          <v-icon>
            {{ mdiPlus }}
          </v-icon>
          Add Teachers
        </v-btn>
      </div>
    </BKModal>
  </div>
</template>
<style lang="scss" scoped>
@import '@/assets/style/mixins';

.teachers {
  display: grid;
  grid-template-rows: auto auto 1fr auto;
  height: 100%;

  .editor-title {
    @include font-h3;

    display: block;
    margin-bottom: 9px;
    padding: 20px 20px 0;
    font-weight: var(--font-weight-bold);

    @media (max-width: 768px) {
      @include font-body1;
    }
  }

  .search {
    padding: 0 20px 12px;
  }

  .list {
    padding: 0 20px;
    overflow: auto;
  }

  .options {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    margin-top: auto;
    padding: 20px;
    border-top: 1px solid var(--color-grey-600);
  }
}

.teachers-title {
  margin: 12px 0;
  color: var(--color-dark-800);
}

.modal-content {
  min-height: 500px;
}

::v-deep.modal-options {
  display: flex;
  justify-content: flex-end;
  width: calc(100% + 40px);
  margin: 0 -20px;
  padding: 20px 20px 0;
  border-top: 1px solid var(--color-grey-600);
}
</style>
